import { createFileRoute, Link } from "@tanstack/react-router";
import { withLocale } from "~/lib/utils/with-locale";
import { useForm } from "react-hook-form";
import z from "zod";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { SignUpSchema } from "~/lib/zod-schemas/auth.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "~/components/ui/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authClient, createAuthError, localizeAuthErrorMessage } from "~/lib/better-auth/client";
import { CheckCircle, LoaderIcon, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { ContentRenderer } from "~/components/ContentRenderer";
import { getCmsPageContent } from "~/lib/server-functions/get-cms-page-content";

export const Route = createFileRoute("/_login/sign-up")({
  component: RouteComponent,
  loader: async () => {
    let cmsContent: Awaited<ReturnType<typeof getCmsPageContent>>["content"] =
      [];
    try {
      const result = await getCmsPageContent({ data: { slug: "/signup" } });
      if (result.content?.length) cmsContent = result.content;
    } catch {
      // No CMS content for this slug
    }
    return { cmsContent };
  },
});

function RouteComponent() {
  const { cmsContent } = Route.useLoaderData();
  return <SignUpPageContent cmsContent={cmsContent} />;
}

export type SignUpCmsContent = Awaited<ReturnType<typeof getCmsPageContent>>["content"];

/** Sign-up form page content. Exported for use under __main/$locale/sign-up. */
export function SignUpPageContent({
  cmsContent,
}: {
  cmsContent: SignUpCmsContent;
}) {
  const [error, setError] = useState("");

  const form = useForm<z.infer<typeof SignUpSchema>>({
    mode: "onBlur",
    resolver: zodResolver(SignUpSchema),
    defaultValues: {
      email: "",
      password: "",
      passwordRepeat: "",
    },
  });

  const signUp = async (data: SignUpSchema) => {
    const { error, data: response } = await authClient.signUp.email({
      email: data.email,
      password: data.password,
      firstName: data.email.split("@")[0],
      lastName: "",
      name: data.email.split("@")[0],
      company: "",
      salutation: "",
      telephone: "",
      preferredLanguage: "german",
      isAdvertiser: false,
    });

    if (error) {
      const authError = createAuthError(
        error,
        "Bei der Registrierung ist ein Fehler aufgetreten. Bitte versuche es erneut.",
        "sign-up",
      );
      setError(localizeAuthErrorMessage(authError.message));
      throw authError;
    }

    return response;
  };

  const signUpMutation = useMutation({
    mutationFn: signUp,
    onSuccess: () => {
      setError("");
    },
  });
  const { isPending: isLoading, isSuccess } = signUpMutation;

  const handleSubmit = async (values: z.infer<typeof SignUpSchema>) => {
    await signUpMutation.mutateAsync(values);
    form.reset();
  };

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center px-4 max-w-[480px] w-full mx-auto">
        <div className="w-full lg:w-[480px] mt-6">
          <Alert>
            <CheckCircle />
            <AlertTitle className="text-2xl leading-none mb-2 line-clamp-none">Registrierung erfolgreich</AlertTitle>
            <AlertDescription>
              Die Registrierung war erfolgreich. Wir haben dir eine E-Mail mit
              einem Link zum Aktivieren deines Accounts geschickt.
              <div className="mt-6 flex justify-end">
                <Button asChild size="lg">
                  <Link to={withLocale("/sign-in")}>Zurück zum Login</Link>
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center px-4 max-w-[480px] w-full mx-auto">
      <h1 className="mt-6 font-medium text-5xl">Registrierung</h1>
      <div className="w-full lg:w-[480px]">
        {error && (
          <Alert className="mt-6" variant={"destructive"}>
            <TriangleAlert />
            <AlertTitle>Fehler bei der Registrierung</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="mt-8 w-full">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="flex flex-col gap-3 w-full"
              noValidate
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <div className="mt-1">
                        <Input
                          {...field}
                          id="email"
                          name="email"
                          type="email"
                          maxLength={50}
                          placeholder="E-Mail"
                          aria-invalid={fieldState.error ? "true" : "false"}
                        />
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <div className="mt-1">
                        <Input
                          {...field}
                          id="password"
                          name="password"
                          type="password"
                          maxLength={50}
                          placeholder="Passwort"
                          aria-invalid={fieldState.error ? "true" : "false"}
                        />
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="passwordRepeat"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <div className="mt-1">
                        <Input
                          {...field}
                          id="passwordRepeat"
                          name="passwordRepeat"
                          type="password"
                          maxLength={100}
                          placeholder="Passwort wiederholen"
                          aria-invalid={fieldState.error ? "true" : "false"}
                        />
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />
              <Button type="submit" variant="default">
                {isLoading ? (
                  <>
                    <LoaderIcon className="animate-spin" />
                    Lädt...
                  </>
                ) : (
                  "Registrieren"
                )}
              </Button>
            </form>
          </Form>
        </div>
        <hr className="bg-black h-0.5 w-full my-7" />
        <div className="flex flex-col gap-3 w-full">
          <Button disabled={true} variant="outline">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 8.79024C18 12.9402 15.79 18.0002 9.13 18.0002C4.12461 18.0334 0.03852 14.0055 0 9.00024C0.03852 3.99485 4.12461 -0.0330555 9.13 0.000204506C11.2007 0.00788451 13.2085 0.712375 14.83 2.0002C14.942 2.09173 15.0109 2.22581 15.02 2.3702C15.0206 2.51629 14.963 2.65661 14.86 2.7602C14.209 3.3554 13.5882 3.98285 13 4.6402C12.8289 4.8285 12.5422 4.85456 12.34 4.7002C11.4161 4.01648 10.2888 3.66418 9.14 3.7002C6.18528 3.7002 3.79 6.09548 3.79 9.05024C3.79 12.0049 6.18528 14.4002 9.14 14.4002C12.14 14.4002 13.41 13.1202 14.07 10.8502H9.5C9.2239 10.8502 9 10.6263 9 10.3502V7.70024C9 7.42404 9.2239 7.20024 9.5 7.20024H17.5C17.7302 7.19874 17.9244 7.37135 17.95 7.60024C17.9871 7.99575 18.0038 8.39294 18 8.79024Z"
                fill="#060606"
              />
            </svg>
            Registrieren mit Google <Badge size={"sm"}>Coming soon</Badge>
          </Button>
          <Button disabled={true} variant="outline">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22 12.3038C22 6.74719 17.5229 2.24268 12 2.24268C6.47715 2.24268 2 6.74719 2 12.3038C2 17.3255 5.65684 21.4879 10.4375 22.2427V15.2121H7.89844V12.3038H10.4375V10.0872C10.4375 7.56564 11.9305 6.1728 14.2146 6.1728C15.3088 6.1728 16.4531 6.36931 16.4531 6.36931V8.84529H15.1922C13.95 8.84529 13.5625 9.6209 13.5625 10.4166V12.3038H16.3359L15.8926 15.2121H13.5625V22.2427C18.3432 21.4879 22 17.3257 22 12.3038Z"
                fill="#060606"
              />
            </svg>
            Registrieren mit Facebook <Badge size={"sm"}>Coming soon</Badge>
          </Button>
          <Button disabled={true} variant="outline">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.3462 6.56481C11.4594 6.66462 11.6104 6.7103 11.76 6.68997C12.7611 6.50537 13.6566 5.95234 14.27 5.13997C14.8664 4.38728 15.2156 3.46873 15.27 2.50997C15.2745 2.35109 15.2046 2.19921 15.0811 2.09921C14.9576 1.9992 14.7945 1.96251 14.64 1.99997C13.6751 2.25193 12.8121 2.79697 12.17 3.55997C11.5599 4.29704 11.2086 5.21395 11.17 6.16997C11.1686 6.32091 11.2329 6.465 11.3462 6.56481ZM18.7025 9.22549C18.1109 9.78066 17.18 10.6542 17.18 12.51C17.1661 14.1669 18.076 15.6938 19.54 16.47C19.7211 16.5518 19.8382 16.7313 19.84 16.93C19.84 17 17.74 22 15.22 22C14.6683 22 14.3006 21.8261 13.8979 21.6357C13.41 21.405 12.8705 21.15 11.89 21.15C10.9236 21.15 10.3828 21.3977 9.88507 21.6257C9.46094 21.82 9.06815 22 8.47001 22C6.15001 22 3.00001 17.14 3.00001 12.75C2.96015 11.51 3.26846 10.2837 3.89001 9.20997C4.76661 7.7243 6.32762 6.77494 8.05001 6.67997C8.86168 6.67997 9.52612 6.91887 10.1546 7.14484C10.7186 7.34761 11.2536 7.53997 11.84 7.53997C12.3999 7.53997 12.8517 7.36465 13.3473 7.17233C13.9495 6.9387 14.6163 6.67997 15.62 6.67997C17.45 6.67997 19.18 7.99997 19.18 8.58997C19.18 8.77744 18.9781 8.96693 18.7025 9.22549Z"
                fill="#060606"
              />
            </svg>
            Registrieren mit Apple <Badge size={"sm"}>Coming soon</Badge>
          </Button>
        </div>
      </div>
      {cmsContent.length > 0 && (
        <div className="w-full lg:w-[480px] mt-10">
          {cmsContent.map((item: { uid: number }) => (
            <ContentRenderer key={item.uid} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
